/**
 * BodyCompositionSummaryCard — Phase 2.2B Session 2
 *
 * Latest measurement + Δ30 / Δ90 trend for BF%, Fat Mass, Lean Mass.
 * Accepts a `rows` array (full body-composition records, descending or
 * ascending; component sorts internally).
 */
import React, { useMemo } from 'react';
import { TrendingDown, TrendingUp, Minus, Beaker } from 'lucide-react';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';
import { useLanguage } from '../../context/LanguageContext';

const fmt = (v, dp = 1, s = '') => v === null || v === undefined ? '—' : `${Number(v).toFixed(dp)}${s}`;

const pickFinal = (r) => ({
  date: r.date,
  method: r.method,
  body_fat_pct: r.body_fat_pct ?? r.body_fat_pct_final ?? null,
  fat_mass_kg: r.fat_mass_kg ?? r.fat_mass_kg_final ?? null,
  lean_mass_kg: r.lean_mass_kg ?? r.lean_mass_kg_final ?? null,
});

const daysBetween = (aIso, bIso) => {
  const a = new Date(aIso).getTime();
  const b = new Date(bIso).getTime();
  return Math.abs((a - b) / (1000 * 60 * 60 * 24));
};

// Find the row closest to `targetDaysAgo` days BEFORE the latest row.
const findBaselineRow = (rows, latest, targetDaysAgo) => {
  if (!latest) return null;
  const latestDate = new Date(latest.date).getTime();
  const targetMs = latestDate - targetDaysAgo * 86400000;
  let best = null;
  let bestDiff = Infinity;
  rows.forEach((r) => {
    if (r === latest) return;
    const t = new Date(r.date).getTime();
    if (t > latestDate) return;
    const diff = Math.abs(t - targetMs);
    if (diff < bestDiff) { bestDiff = diff; best = r; }
  });
  return best;
};

const DeltaBadge = ({ baseline, latest, dp = 1, suffix = '', invertColors = false, testid }) => {
  if (baseline === null || baseline === undefined || latest === null || latest === undefined) {
    return <span className="text-zinc-600 text-xs" data-testid={testid}>—</span>;
  }
  const delta = latest - baseline;
  const isZero = Math.abs(delta) < 0.05;
  let color;
  if (isZero) color = 'text-zinc-400';
  else if (invertColors) color = delta > 0 ? 'text-amber-400' : 'text-emerald-400';
  else color = delta > 0 ? 'text-emerald-400' : 'text-amber-400';
  const Icon = isZero ? Minus : (delta > 0 ? TrendingUp : TrendingDown);
  return (
    <span className={`inline-flex items-center gap-1 text-xs font-medium ${color}`} data-testid={testid}>
      <Icon className="w-3 h-3" />
      {delta > 0 ? '+' : ''}{delta.toFixed(dp)}{suffix}
    </span>
  );
};

const Metric = ({ label, value, suffix, dp, baseline30, baseline90, latest, invertColors, testidPrefix }) => (
  <div className="bg-zinc-950/50 border border-zinc-800 rounded p-3" data-testid={`${testidPrefix}-block`}>
    <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">{label}</div>
    <div className="text-2xl font-bold text-zinc-100" data-testid={`${testidPrefix}-value`}>{fmt(value, dp, suffix)}</div>
    <div className="flex flex-col gap-0.5 mt-2">
      <div className="flex items-center justify-between text-[11px]">
        <span className="text-zinc-500">Δ30d</span>
        <DeltaBadge baseline={baseline30} latest={latest} dp={dp} suffix={suffix} invertColors={invertColors} testid={`${testidPrefix}-d30`} />
      </div>
      <div className="flex items-center justify-between text-[11px]">
        <span className="text-zinc-500">Δ90d</span>
        <DeltaBadge baseline={baseline90} latest={latest} dp={dp} suffix={suffix} invertColors={invertColors} testid={`${testidPrefix}-d90`} />
      </div>
    </div>
  </div>
);

const BodyCompositionSummaryCard = ({ rows }) => {
  const { language } = useLanguage();

  const { latest, b30, b90 } = useMemo(() => {
    const arr = (rows || []).map(pickFinal).filter((r) => r.date).sort((a, b) => b.date.localeCompare(a.date));
    if (arr.length === 0) return { latest: null, b30: null, b90: null };
    const latestRow = arr[0];
    const b30Row = findBaselineRow(arr, latestRow, 30);
    const b90Row = findBaselineRow(arr, latestRow, 90);
    return { latest: latestRow, b30: b30Row, b90: b90Row };
  }, [rows]);

  return (
    <Card className="bg-zinc-900 border-zinc-800 p-5" data-testid="bodycomp-summary-card">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Beaker className="w-5 h-5 text-emerald-400" />
          <h3 className="text-lg font-semibold text-zinc-100">
            {language === 'IT' ? 'Composizione corporea' : 'Body composition'}
          </h3>
        </div>
        {latest && (
          <div className="flex items-center gap-2 text-xs text-zinc-400" data-testid="bodycomp-latest-info">
            <span>{latest.date}</span>
            <Badge className="bg-zinc-800 text-zinc-200 border-zinc-700 text-[10px]" data-testid="bodycomp-latest-method">
              {latest.method}
            </Badge>
          </div>
        )}
      </div>

      {!latest ? (
        <div className="text-sm text-zinc-500 py-4 text-center" data-testid="bodycomp-empty">
          {language === 'IT' ? 'Nessuna misurazione registrata.' : 'No measurements recorded yet.'}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <Metric
            label="BF %" value={latest.body_fat_pct} suffix=" %" dp={1}
            baseline30={b30?.body_fat_pct} baseline90={b90?.body_fat_pct}
            latest={latest.body_fat_pct}
            invertColors testidPrefix="bodycomp-bf"
          />
          <Metric
            label={language === 'IT' ? 'Massa grassa' : 'Fat mass'} value={latest.fat_mass_kg} suffix=" kg" dp={1}
            baseline30={b30?.fat_mass_kg} baseline90={b90?.fat_mass_kg}
            latest={latest.fat_mass_kg}
            invertColors testidPrefix="bodycomp-fm"
          />
          <Metric
            label={language === 'IT' ? 'Massa magra' : 'Lean mass'} value={latest.lean_mass_kg} suffix=" kg" dp={1}
            baseline30={b30?.lean_mass_kg} baseline90={b90?.lean_mass_kg}
            latest={latest.lean_mass_kg}
            testidPrefix="bodycomp-lean"
          />
        </div>
      )}
    </Card>
  );
};

export default BodyCompositionSummaryCard;
